import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Star,
  StarOff,
  ArrowLeft,
  Eye,
  EyeOff,
  Flame,
  Wrench,
  Zap,
  Droplets,
  Home,
  Building,
  Hammer,
  Cog,
  Shield,
  Heart,
  Target,
  Award,
  Lightbulb,
  Circle,
  Square,
  Triangle
} from "lucide-react"
import { serviceAPI } from "../services/serviceService"

function AdminServices() {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [showInactive, setShowInactive] = useState(true)
  const [editingService, setEditingService] = useState(null)
  const [addingService, setAddingService] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    fetchServices()
  }, [])

  const fetchServices = async () => {
    try {
      setLoading(true)
      const response = await serviceAPI.getAllServices()
      setServices(response)
    } catch (error) {
      setError("Hizmetler yüklenirken bir hata oluştu")
      console.error("Fetch services error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleEditService = (service) => {
    setEditingService({
      ...service,
      details: [...service.details], // Array'i kopyala
      tags: service.tags ? [...service.tags] : [] // Tags array'ini kopyala
    })
  }

  const handleAddService = () => {
    setAddingService({
      title: "",
      description: "",
      price_start: "",
      icon: "Settings",
      is_featured: false,
      is_active: true,
      details: [""],
      tags: []
    })
  }

  const handleUpdateService = async (e) => {
    e.preventDefault()
    
    try {
      const updateData = {
        title: editingService.title,
        description: editingService.description,
        price_start: editingService.price_start,
        icon: editingService.icon,
        is_featured: editingService.is_featured,
        is_active: editingService.is_active,
        details: editingService.details.filter(detail => detail.trim() !== ""),
        tags: editingService.tags || []
      }

      await serviceAPI.updateService(editingService.id, updateData)
      setEditingService(null)
      fetchServices()
    } catch (error) {
      setError("Hizmet güncellenirken bir hata oluştu")
    }
  }

  const handleCreateService = async (e) => {
    e.preventDefault()
    
    try {
      const serviceData = {
        title: addingService.title,
        description: addingService.description,
        price_start: addingService.price_start,
        icon: addingService.icon,
        is_featured: addingService.is_featured,
        is_active: addingService.is_active,
        details: addingService.details.filter(detail => detail.trim() !== ""),
        tags: addingService.tags || []
      }

      await serviceAPI.addService(serviceData)
      setAddingService(null)
      fetchServices()
    } catch (error) {
      setError("Hizmet oluşturulurken bir hata oluştu")
    }
  }

  const handleDeleteService = async (serviceId) => {
    if (window.confirm("Bu hizmeti silmek istediğinizden emin misiniz?")) {
      try {
        await serviceAPI.deleteService(serviceId)
        fetchServices()
      } catch (error) {
        setError("Hizmet silinirken bir hata oluştu")
      }
    }
  }

  const handleToggleFeatured = async (serviceId) => {
    try {
      await serviceAPI.toggleFeatured(serviceId)
      fetchServices()
    } catch (error) {
      setError("Öne çıkarılma durumu güncellenirken bir hata oluştu")
    }
  }

  const addDetailField = (isEditing = false) => {
    if (isEditing) {
      setEditingService(prev => ({
        ...prev,
        details: [...prev.details, ""]
      }))
    } else {
      setAddingService(prev => ({
        ...prev,
        details: [...prev.details, ""]
      }))
    }
  }

  const removeDetailField = (index, isEditing = false) => {
    if (isEditing) {
      setEditingService(prev => ({
        ...prev,
        details: prev.details.filter((_, i) => i !== index)
      }))
    } else {
      setAddingService(prev => ({
        ...prev,
        details: prev.details.filter((_, i) => i !== index)
      }))
    }
  }

  const updateDetailField = (index, value, isEditing = false) => {
    if (isEditing) {
      setEditingService(prev => ({
        ...prev,
        details: prev.details.map((detail, i) => i === index ? value : detail)
      }))
    } else {
      setAddingService(prev => ({
        ...prev,
        details: prev.details.map((detail, i) => i === index ? value : detail)
      }))
    }
  }

  const addTagField = (isEditing = false) => {
    if (isEditing) {
      setEditingService(prev => ({
        ...prev,
        tags: [...(prev.tags || []), ""]
      }))
    } else {
      setAddingService(prev => ({
        ...prev,
        tags: [...(prev.tags || []), ""]
      }))
    }
  }

  const removeTagField = (index, isEditing = false) => {
    if (isEditing) {
      setEditingService(prev => ({
        ...prev,
        tags: (prev.tags || []).filter((_, i) => i !== index)
      }))
    } else {
      setAddingService(prev => ({
        ...prev,
        tags: (prev.tags || []).filter((_, i) => i !== index)
      }))
    }
  }

  const updateTagField = (index, value, isEditing = false) => {
    if (isEditing) {
      setEditingService(prev => ({
        ...prev,
        tags: (prev.tags || []).map((tag, i) => i === index ? value : tag)
      }))
    } else {
      setAddingService(prev => ({
        ...prev,
        tags: (prev.tags || []).map((tag, i) => i === index ? value : tag)
      }))
    }
  }

  const filteredServices = services.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = showInactive || service.is_active
    
    return matchesSearch && matchesStatus
  })

  const iconOptions = [
    { name: "Settings", icon: Settings },
    { name: "Flame", icon: Flame },
    { name: "Wrench", icon: Wrench },
    { name: "Zap", icon: Zap },
    { name: "Droplets", icon: Droplets },
    { name: "Home", icon: Home },
    { name: "Building", icon: Building },
    { name: "Hammer", icon: Hammer },
    { name: "Cog", icon: Cog },
    { name: "Shield", icon: Shield },
    { name: "Heart", icon: Heart },
    { name: "Target", icon: Target },
    { name: "Award", icon: Award },
    { name: "Lightbulb", icon: Lightbulb },
    { name: "Circle", icon: Circle },
    { name: "Square", icon: Square },
    { name: "Triangle", icon: Triangle }
  ]

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(option => option.name === iconName)
    return iconOption ? iconOption.icon : Settings
  }

  const renderIcon = (iconName) => {
    const IconComponent = getIconComponent(iconName)
    return <IconComponent className="h-5 w-5 text-green-600" />
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Hizmetler yükleniyor...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Geri</span>
              </button>
              <div className="flex items-center space-x-2">
                <Settings className="h-6 w-6 text-green-600" />
                <h1 className="text-xl font-bold text-gray-900">Hizmet Yönetimi</h1>
              </div>
            </div>
            <button
              onClick={handleAddService}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Yeni Hizmet Ekle</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hizmet ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Pasif hizmetleri göster</span>
              </label>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) => (
            <div key={service.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                      {renderIcon(service.icon)}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 truncate">{service.title}</h3>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    <button
                      onClick={() => handleToggleFeatured(service.id)}
                      className={`p-1 rounded ${service.is_featured ? 'text-yellow-500' : 'text-gray-400'}`}
                      title={service.is_featured ? 'Öne çıkarılmış' : 'Öne çıkar'}
                    >
                      {service.is_featured ? <Star className="h-4 w-4" /> : <StarOff className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleEditService(service)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteService(service.id)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-3">{service.description}</p>
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-green-600 font-semibold">{service.price_start}</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    service.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {service.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>

                {service.details && service.details.length > 0 && (
                  <div className="border-t pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Detaylar:</h4>
                    <ul className="space-y-1">
                      {service.details.map((detail, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-center">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {service.tags && service.tags.length > 0 && (
                  <div className="border-t pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Etiketler:</h4>
                    <div className="flex flex-wrap gap-1">
                      {service.tags.map((tag, index) => (
                        <span 
                          key={index} 
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Edit Service Modal */}
      {editingService && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Hizmet Düzenle
              </h3>
              
              <form onSubmit={handleUpdateService} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Başlık *</label>
                    <input
                      type="text"
                      value={editingService.title}
                      onChange={(e) => setEditingService({...editingService, title: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">İkon</label>
                    <select
                      value={editingService.icon}
                      onChange={(e) => setEditingService({...editingService, icon: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                    >
                      {iconOptions.map(option => (
                        <option key={option.name} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                                         <div className="mt-2 flex items-center space-x-2">
                       <span className="text-sm text-gray-500">Seçili ikon:</span>
                       {renderIcon(editingService.icon)}
                     </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama *</label>
                  <textarea
                    value={editingService.description}
                    onChange={(e) => setEditingService({...editingService, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fiyat Başlangıcı *</label>
                  <input
                    type="text"
                    value={editingService.price_start}
                    onChange={(e) => setEditingService({...editingService, price_start: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingService.is_featured}
                      onChange={(e) => setEditingService({...editingService, is_featured: e.target.checked})}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Öne çıkar</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingService.is_active}
                      onChange={(e) => setEditingService({...editingService, is_active: e.target.checked})}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Aktif</span>
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Detaylar</label>
                  {editingService.details.map((detail, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={detail}
                        onChange={(e) => updateDetailField(index, e.target.value, true)}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Detay açıklaması"
                      />
                      <button
                        type="button"
                        onClick={() => removeDetailField(index, true)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addDetailField(true)}
                    className="text-green-600 hover:text-green-700 text-sm"
                  >
                    + Detay Ekle
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Etiketler</label>
                  {(editingService.tags || []).map((tag, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={tag}
                        onChange={(e) => updateTagField(index, e.target.value, true)}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Etiket"
                      />
                      <button
                        type="button"
                        onClick={() => removeTagField(index, true)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addTagField(true)}
                    className="text-green-600 hover:text-green-700 text-sm"
                  >
                    + Etiket Ekle
                  </button>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Güncelle
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingService(null)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Service Modal */}
      {addingService && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Yeni Hizmet Ekle
              </h3>
              
              <form onSubmit={handleCreateService} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Başlık *</label>
                    <input
                      type="text"
                      value={addingService.title}
                      onChange={(e) => setAddingService({...addingService, title: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">İkon</label>
                    <select
                      value={addingService.icon}
                      onChange={(e) => setAddingService({...addingService, icon: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                    >
                      {iconOptions.map(option => (
                        <option key={option.name} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                                         <div className="mt-2 flex items-center space-x-2">
                       <span className="text-sm text-gray-500">Seçili ikon:</span>
                       {renderIcon(addingService.icon)}
                     </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama *</label>
                  <textarea
                    value={addingService.description}
                    onChange={(e) => setAddingService({...addingService, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fiyat Başlangıcı *</label>
                  <input
                    type="text"
                    value={addingService.price_start}
                    onChange={(e) => setAddingService({...addingService, price_start: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>
                
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={addingService.is_featured}
                      onChange={(e) => setAddingService({...addingService, is_featured: e.target.checked})}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Öne çıkar</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={addingService.is_active}
                      onChange={(e) => setAddingService({...addingService, is_active: e.target.checked})}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Aktif</span>
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Detaylar</label>
                  {addingService.details.map((detail, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={detail}
                        onChange={(e) => updateDetailField(index, e.target.value, false)}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Detay açıklaması"
                      />
                      <button
                        type="button"
                        onClick={() => removeDetailField(index, false)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addDetailField(false)}
                    className="text-green-600 hover:text-green-700 text-sm"
                  >
                    + Detay Ekle
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Etiketler</label>
                  {(addingService.tags || []).map((tag, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={tag}
                        onChange={(e) => updateTagField(index, e.target.value, false)}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                        placeholder="Etiket"
                      />
                      <button
                        type="button"
                        onClick={() => removeTagField(index, false)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addTagField(false)}
                    className="text-green-600 hover:text-green-700 text-sm"
                  >
                    + Etiket Ekle
                  </button>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Hizmet Ekle
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddingService(null)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminServices 